import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { crearPack } from 'scripts/crearPack';
import { actualizarPack } from 'scripts/actualizarPack';
import { borrarPack } from 'scripts/borrarPack';
import { obtenerPacks } from 'scripts/obtenerPacks';
import { calcularPrecioPack } from 'scripts/calcularPrecioPack';
import { setPacks, addPack, updatePack, removePack, updatePackPricing } from 'store/packSlice';
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Button,
	Paper,
	TextField,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Box,
	Grid,
	InputAdornment,
	FormControlLabel,
	Checkbox,
	Typography,
	Card,
	CardContent,
	Chip,
	IconButton,
	Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CalculateIcon from '@mui/icons-material/Calculate';
import { subirImagenAFirebase } from 'scripts/firebaseUpload';
import { toast } from 'react-toastify';
import CustomDialog from 'helpers/Dialog';

const PackManagement = () => {
	const dispatch = useDispatch();
	const packs = useSelector((state) => state.packs);
	const productos = useSelector((state) => state.productos);
	const [loading, setLoading] = useState(false);

	const [searchTerm, setSearchTerm] = useState('');
	const [open, setOpen] = useState(false);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [selectedPackId, setSelectedPackId] = useState(null);
	const [productosSeleccionados, setProductosSeleccionados] = useState([]);
	const [searchProductos, setSearchProductos] = useState('');

	const [newPack, setNewPack] = useState({
		id: null,
		titulo: '',
		nombre: '',
		descripcion: '',
		imagen: null,
		activo: true,
		precioFinal: '',
		productos: [],
	});

	// Fetch data on component mount
	useEffect(() => {
		fetchPacks();
	}, []);

	const fetchPacks = async () => {
		try {
			setLoading(true);
			const packsData = await obtenerPacks(true); // Include inactive packs for admin
			dispatch(setPacks(packsData));
			setLoading(false);
		} catch (error) {
			setLoading(false);
			toast.error('Error al cargar los packs');
		}
	};

	// Filter functions with null checks
	const filteredPacks = (packs || []).filter(
		(pack) => pack.titulo && pack.titulo.toLowerCase().includes(searchTerm.toLowerCase()),
	);

	const filteredProductos = (productos || []).filter(
		(producto) => producto.titulo && producto.titulo.toLowerCase().includes(searchProductos.toLowerCase()),
	);

	// Handle form changes
	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setNewPack({ ...newPack, [name]: value });
	};

	const handleImageChange = (e) => {
		const file = e.target.files[0];
		if (file) {
			setNewPack((prevState) => ({
				...prevState,
				imagen: file,
			}));
		}
	};

	const handleProductoChange = (productoId, cantidad) => {
		const updatedProductos = [...productosSeleccionados];
		const existingIndex = updatedProductos.findIndex((p) => p.productoId === productoId);

		if (cantidad > 0) {
			if (existingIndex >= 0) {
				updatedProductos[existingIndex].cantidad = cantidad;
			} else {
				updatedProductos.push({ productoId, cantidad });
			}
		} else {
			if (existingIndex >= 0) {
				updatedProductos.splice(existingIndex, 1);
			}
		}

		setProductosSeleccionados(updatedProductos);
		calculatePreviewPrice(updatedProductos);
	};

	const calculatePreviewPrice = (productosSelected) => {
		let total = 0;
		if (productosSelected && productos) {
			productosSelected.forEach(({ productoId, cantidad }) => {
				const producto = productos.find((p) => p.id === productoId);
				if (producto) {
					total += producto.precio * cantidad;
				}
			});
		}
		return total;
	};

	// Dialog handlers
	const handleOpen = (pack = null) => {
		if (pack) {
			setNewPack({
				id: pack.id,
				titulo: pack.titulo,
				nombre: pack.nombre || pack.titulo,
				descripcion: pack.descripcion,
				imagen: pack.imagen,
				activo: pack.activo,
				precioFinal: pack.precio_final_personalizado || '',
				productos: pack.productos || [],
			});

			// Set selected products
			const productosData =
				pack.productos?.map((p) => ({
					productoId: p.id,
					cantidad: p.cantidad,
				})) || [];
			setProductosSeleccionados(productosData);
		} else {
			setNewPack({
				id: null,
				titulo: '',
				nombre: '',
				descripcion: '',
				imagen: null,
				activo: true,
				precioFinal: '',
				productos: [],
			});
			setProductosSeleccionados([]);
		}
		setOpen(true);
	};

	const handleClose = () => {
		setOpen(false);
		setNewPack({
			id: null,
			titulo: '',
			nombre: '',
			descripcion: '',
			imagen: null,
			activo: true,
			precioFinal: '',
			productos: [],
		});
		setProductosSeleccionados([]);
	};

	const handleOpenDialog = (packId) => {
		setSelectedPackId(packId);
		setDialogOpen(true);
	};

	const handleCloseDialog = () => {
		setSelectedPackId(null);
		setDialogOpen(false);
	};

	const handleConfirmDialog = async () => {
		try {
			await borrarPack(selectedPackId, sessionStorage.getItem('token'));
			dispatch(removePack(selectedPackId));
			toast.success('Pack eliminado correctamente');
		} catch (error) {
			toast.error('Error al eliminar el pack');
		}
		setDialogOpen(false);
	};

	// Save pack
	const handleSavePack = async () => {
		if (!newPack.titulo || !newPack.descripcion || productosSeleccionados.length === 0) {
			toast.error('Por favor, complete todos los campos obligatorios y seleccione al menos un producto');
			return;
		}

		try {
			setLoading(true);

			let imageUrl = newPack.imagen;

			// Upload image if it's a new file
			if (newPack.imagen && typeof newPack.imagen !== 'string') {
				imageUrl = await subirImagenAFirebase(newPack.imagen);
			}

			const packData = {
				titulo: newPack.titulo,
				nombre: newPack.nombre || newPack.titulo,
				descripcion: newPack.descripcion,
				imagen: imageUrl || '',
				activo: newPack.activo,
				precioFinal: newPack.precioFinal ? parseFloat(newPack.precioFinal) : null,
				productos: productosSeleccionados,
			};

			let savedPack;
			if (newPack.id === null) {
				savedPack = await crearPack(packData, sessionStorage.getItem('token'));
				dispatch(addPack(savedPack));
				toast.success('Pack creado correctamente');
			} else {
				savedPack = await actualizarPack(newPack.id, packData, sessionStorage.getItem('token'));
				dispatch(updatePack(savedPack));
				toast.success('Pack actualizado correctamente');
			}

			setLoading(false);
			handleClose();
		} catch (error) {
			setLoading(false);
			toast.error('Error al guardar el pack');
		}
	};

	// Recalculate price
	const handleRecalculatePrice = async (packId) => {
		try {
			const result = await calcularPrecioPack(packId, sessionStorage.getItem('token'));
			dispatch(updatePackPricing(result));
			toast.success('Precio recalculado correctamente');
		} catch (error) {
			toast.error('Error al recalcular el precio');
		}
	};

	const previewPrice = calculatePreviewPrice(productosSeleccionados);
	const finalPrice = newPack.precioFinal ? parseFloat(newPack.precioFinal) : previewPrice;
	const savings = previewPrice - finalPrice;

	return (
		<div style={{ marginTop: '20px' }}>
			{/* Header with search and add button */}
			<Box sx={{ marginBottom: 2, paddingBottom: 2, borderBottom: '2px solid #ccc' }}>
				<Grid container spacing={2} alignItems="center">
					<Grid item xs={12} sm={2} md={1}>
						<Button variant="contained" color="success" onClick={() => handleOpen()}>
							<AddIcon />
						</Button>
					</Grid>
					<Grid item xs={12} sm={6} md={4}>
						<TextField
							label="Buscar packs"
							variant="outlined"
							fullWidth
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<SearchIcon />
									</InputAdornment>
								),
							}}
						/>
					</Grid>
				</Grid>
			</Box>

			{/* Packs table */}
			<TableContainer component={Paper} sx={{ maxHeight: 500, overflowY: 'auto' }}>
				<Table>
					<TableHead sx={{ position: 'sticky', top: 0, backgroundColor: '#FFFFFF', zIndex: 1 }}>
						<TableRow>
							<TableCell>Título</TableCell>
							<TableCell>Descripción</TableCell>
							<TableCell>Precio Calculado</TableCell>
							<TableCell>Precio Final</TableCell>
							<TableCell>Ahorro</TableCell>
							<TableCell>Activo</TableCell>
							<TableCell>Productos</TableCell>
							<TableCell>Acciones</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{filteredPacks.length > 0 ? (
							filteredPacks.map((pack) => (
								<TableRow key={pack.id}>
									<TableCell>{pack.titulo}</TableCell>
									<TableCell>{pack.descripcion}</TableCell>
									<TableCell>${pack.precio_calculado}</TableCell>
									<TableCell>${pack.precio_mostrar}</TableCell>
									<TableCell>
										{pack.ahorro > 0 && (
											<Chip label={`$${pack.ahorro}`} color="success" size="small" />
										)}
									</TableCell>
									<TableCell>{pack.activo ? 'Sí' : 'No'}</TableCell>
									<TableCell>
										<Typography variant="body2">{pack.productos?.length || 0} productos</Typography>
									</TableCell>
									<TableCell>
										<IconButton color="primary" onClick={() => handleOpen(pack)} size="small">
											<EditIcon />
										</IconButton>
										<IconButton
											color="info"
											onClick={() => handleRecalculatePrice(pack.id)}
											size="small"
										>
											<CalculateIcon />
										</IconButton>
										<IconButton
											color="error"
											onClick={() => handleOpenDialog(pack.id)}
											size="small"
										>
											<DeleteIcon />
										</IconButton>
									</TableCell>
								</TableRow>
							))
						) : (
							<TableRow>
								<TableCell colSpan={8} style={{ textAlign: 'center' }}>
									No hay packs disponibles.
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>

			{/* Pack creation/edit dialog */}
			<Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
				<DialogTitle>{newPack.id ? 'Editar Pack' : 'Nuevo Pack'}</DialogTitle>
				<DialogContent>
					<Grid container spacing={2}>
						{/* Basic pack information */}
						<Grid item xs={12} md={6}>
							<TextField
								name="titulo"
								label="Título"
								variant="outlined"
								value={newPack.titulo}
								onChange={handleInputChange}
								fullWidth
								margin="normal"
								required
							/>
							<TextField
								name="descripcion"
								label="Descripción"
								variant="outlined"
								value={newPack.descripcion}
								onChange={handleInputChange}
								fullWidth
								multiline
								rows={3}
								margin="normal"
								required
							/>
							<TextField
								name="precioFinal"
								label="Precio Final (opcional)"
								variant="outlined"
								value={newPack.precioFinal}
								onChange={handleInputChange}
								fullWidth
								type="number"
								margin="normal"
								helperText="Dejar vacío para usar precio calculado automáticamente"
							/>
							<Box sx={{ mt: 2 }}>
								<Typography variant="body2" gutterBottom>
									Imagen del Pack
								</Typography>
								<input
									type="file"
									accept="image/*"
									onChange={handleImageChange}
									style={{ marginBottom: '10px', display: 'block' }}
								/>
							</Box>
							<FormControlLabel
								control={
									<Checkbox
										checked={newPack.activo}
										onChange={(e) => setNewPack({ ...newPack, activo: e.target.checked })}
										color="primary"
									/>
								}
								label="Pack Activo"
							/>
						</Grid>

						{/* Price preview */}
						<Grid item xs={12} md={6}>
							<Card sx={{ mt: 2 }}>
								<CardContent>
									<Typography variant="h6" gutterBottom>
										Vista Previa de Precios
									</Typography>
									<Typography variant="body1">
										Precio Calculado: <strong>${previewPrice}</strong>
									</Typography>
									<Typography variant="body1">
										Precio Final: <strong>${finalPrice}</strong>
									</Typography>
									{savings > 0 && (
										<Typography variant="body1" color="success.main">
											Ahorro: <strong>${savings}</strong>
										</Typography>
									)}
								</CardContent>
							</Card>
						</Grid>

						{/* Product selection */}
						<Grid item xs={12}>
							<Divider sx={{ my: 2 }} />
							<Typography variant="h6" gutterBottom>
								Seleccionar Productos
							</Typography>
							<TextField
								variant="outlined"
								fullWidth
								placeholder="Buscar productos..."
								value={searchProductos}
								onChange={(e) => setSearchProductos(e.target.value)}
								margin="normal"
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<SearchIcon />
										</InputAdornment>
									),
								}}
							/>
							<Box
								sx={{
									maxHeight: 300,
									overflowY: 'auto',
									border: '1px solid #ccc',
									borderRadius: 1,
									p: 1,
								}}
							>
								{filteredProductos.map((producto) => {
									const selectedProduct = productosSeleccionados.find(
										(p) => p.productoId === producto.id,
									);
									const cantidad = selectedProduct ? selectedProduct.cantidad : 0;

									return (
										<Box
											key={producto.id}
											sx={{
												display: 'flex',
												alignItems: 'center',
												mb: 1,
												p: 1,
												borderBottom: '1px solid #eee',
											}}
										>
											<Box sx={{ flexGrow: 1 }}>
												<Typography variant="body2">
													{producto.titulo} - ${producto.precio}
												</Typography>
											</Box>
											<TextField
												type="number"
												size="small"
												value={cantidad}
												onChange={(e) =>
													handleProductoChange(producto.id, parseInt(e.target.value) || 0)
												}
												inputProps={{ min: 0, max: 10 }}
												sx={{ width: 80 }}
											/>
										</Box>
									);
								})}
							</Box>
						</Grid>
					</Grid>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose} color="secondary">
						Cancelar
					</Button>
					<Button onClick={handleSavePack} color="primary" disabled={loading}>
						{newPack.id ? 'Guardar Cambios' : 'Crear Pack'}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Delete confirmation dialog */}
			<CustomDialog
				open={dialogOpen}
				title="Confirmar eliminación"
				content="¿Está seguro de que desea eliminar este pack?"
				onClose={handleCloseDialog}
				onConfirm={handleConfirmDialog}
			/>
		</div>
	);
};

export default PackManagement;
